<template>
  <e-expansion-panels v-if="loading" :model-value="[0]">
    <e-expansion-panel>
      <template #title>
        <skeleton-loader height="24px" width="150px" />
      </template>
      <e-data-items :loading="loading" :model-value="[]" :items="[]" :headers="headers" />
    </e-expansion-panel>
  </e-expansion-panels>

  <e-alert
    v-if="isEmptyPlacingVariants && (filterRoomCategory || filterTouristType)"
    variant="error"
    thin
    class="mb-1 mt-1 w-100"
  >
    Для некоторых категорий номеров не выбраны варианты размещения
  </e-alert>

  <div
    v-for="(roomCategory, roomCategoryIndex) in items"
    :class="{ 'mt-10': roomCategoryIndex !== 0 }"
    :key="roomCategoryIndex"
  >
    <e-expansion-panels v-if="!filterRoomCategory || filterRoomCategory === roomCategory.roomCid" :model-value="[0]">
      <e-expansion-panel>
        <template #title> {{ roomCategory.roomName }}</template>
        <template v-if="isDirty && !roomCategory.selected.length" #titleSecondary>
          <e-alert variant="error" thin class="mb-1 mt-1 w-100">
            Для категории номера должен быть хотя бы один вариант размещения
          </e-alert>
        </template>
        <e-data-items
          v-model="roomCategory.selected"
          return-object
          :items="roomCategory.placingVariants"
          :headers="headers"
          :show-only-selected="filterCheckedOnly"
          :row-props="filterTableRow"
          :styled-header="roomCategory.isMassOperationActive"
          :disabled="disabled"
          @update:model-value="onSelectedUpdate(roomCategory)"
        >
          <template v-slot:[`header.data-table-select`]="props">
            <e-checkbox
              :model-value="props.allSelected"
              :square-selected="props.someSelected"
              @update:modelValue="toggleSelectAll(roomCategory, $event)"
            />
          </template>

          <template v-slot:[`item.data-table-select`]="{ isSelected, internalItem, toggleSelect }">
            <e-checkbox
              :model-value="isSelected(internalItem)"
              :disabled="(roomCategory.selected.length <= 1 && isSelected(internalItem)) || disabled"
              @update:modelValue="toggleSelect(internalItem, $event)"
            />
          </template>

          <template v-slot:[`item.tarification.defaultAvailable`]="{ item, isSelected, internalItem, value }">
            <template v-if="tariffPage">
              <template v-if="disabled">
                <e-icon v-if="value" name="check" />
              </template>
              <e-checkbox
                v-else-if="item.tarification"
                :disabled="!isSelected(internalItem)"
                v-model="item.tarification.defaultAvailable"
              />
            </template>
            <e-checkbox
              v-else-if="isSelected(internalItem) && item.tarification"
              v-model="item.tarification.defaultAvailable"
            />
          </template>

          <template v-if="!disabled" v-slot:[`item.dataItemSecondRow`]="{ item }">
            <div v-if="item.isLastVariant" class="paragraph-xs text-secondary d-flex align-center font-400">
              <e-icon name="info" :size="16" class="text-blue mr-2" />
              Для категории номера должен быть хотя бы один вариант размещения
            </div>
          </template>

          <template v-slot:[`item.name`]="{ item, value }">
            <div class="placing-variant-table__name" :class="{ 'with-badge': item.isConflicted }">
              <span>{{ value }}</span>
              <help-tooltip>
                <placing-variants-tooltip :placing-variant="item.placingVariant" />
              </help-tooltip>
            </div>
          </template>
          <template v-slot:[`item.tarification.maxNoSeatQuantity`]="{ item, isSelected, internalItem, value }">
            <span v-if="disabled">
              {{ value }}
            </span>
            <e-input
              v-else-if="(tariffPage || isSelected(internalItem)) && item.tarification"
              v-model:number-value="item.tarification.maxNoSeatQuantity"
              :disabled="!isSelected(internalItem)"
              :rules="[required()]"
              :maska="placesMask"
            />
          </template>
          <template v-slot:[`item.tarification.pricePerRoom`]="{ item, isSelected, internalItem, value }">
            <template v-if="tariffPage && disabled">
              <span v-if="value">за номер</span>
              <span v-else>за туриста</span>
            </template>
            <e-select
              v-else-if="item.tarification && (tariffPage || isSelected(internalItem))"
              item-value="value"
              v-model="item.tarification.pricePerRoom"
              :items="[
                {
                  title: 'за номер',
                  value: true,
                },
                {
                  title: 'за туриста',
                  value: false,
                },
              ]"
              :disabled="!isSelected(internalItem)"
            />
          </template>
          <template v-slot:[`item.tarification.isNeedConfirm`]="{ item, isSelected, internalItem, value }">
            <template v-if="tariffPage">
              <template v-if="disabled">
                <e-icon v-if="value" name="check" />
              </template>
              <e-checkbox
                v-else-if="item.tarification"
                :disabled="!isSelected(internalItem)"
                v-model="item.tarification.isNeedConfirm"
              />
            </template>
            <e-checkbox
              v-else-if="item.tarification && isSelected(internalItem)"
              v-model="item.tarification.isNeedConfirm"
            />
          </template>
        </e-data-items>
      </e-expansion-panel>
    </e-expansion-panels>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import type { PVMPlacingVariant, PVMPlacingVariantsItem } from '@/parts/tariffs/types/tariffs';
import EDataItems from '@/core/components/common/data-items/EDataItems.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import {
  EAlert,
  ECheckbox,
  EExpansionPanel,
  EExpansionPanels,
  EIcon,
  EInput,
  ESelect,
  HelpTooltip,
} from '@/core/components/ui';
import { placesMask } from '@/core/consts/masks';
import { type DataTableHeaders, EditableHeaderType } from '@/core/types/components/table';
import { required } from '@/core/utils/validation';
import PlacingVariantsTooltip from '@/parts/tariffs/components/PlacingVariantsTooltip.vue';
import { compareTouristTypes } from '@/parts/tariffs/utils/tariffs';

const props = defineProps<{
  filterRoomCategory: string | null;
  filterTouristType: string | null;
  filterCheckedOnly?: boolean;
  filterConflictedOnly: boolean;
  isDirty?: boolean;
  tariffPage?: boolean;
  disabled?: boolean;
  loading?: boolean;
}>();

const $display = useDisplay();

const items = defineModel<PVMPlacingVariantsItem[]>({ required: true });

const filterTableRow = (data: { item: PVMPlacingVariant }) => {
  if (
    (props.filterTouristType && !compareTouristTypes(data.item, props.filterTouristType)) ||
    (props.filterConflictedOnly && !data.item.isConflicted)
  ) {
    return {
      class: 'd-none',
    };
  }

  return {};
};

const isEmptyPlacingVariants = computed(
  () => props.isDirty && items.value.some((roomCategory) => !roomCategory.selected.length),
);

const onSelectedUpdate = (newValue: PVMPlacingVariantsItem) => {
  if (!newValue.selected.length) {
    const item = newValue.placingVariants[0];
    if (item) {
      item.isLastVariant = true;
      newValue.selected.push(item);
    }
  } else if (newValue.selected.length === 1) {
    const item = newValue.selected[0];

    item.isLastVariant = true;
  } else {
    newValue.placingVariants.forEach((variant) => (variant.isLastVariant = false));
  }
};

const toggleSelectAll = (roomCategory: PVMPlacingVariantsItem, value: boolean) => {
  if (props.disabled) return;

  const { placingVariants } = roomCategory;

  if (value) {
    roomCategory.selected = [...placingVariants];
  } else {
    roomCategory.selected = [];
  }

  onSelectedUpdate(roomCategory);
};

const headers = computed<DataTableHeaders<PVMPlacingVariant>>(() => [
  {
    key: 'name',
    title: 'Вариант размещения',
  },
  {
    key: 'tarification.defaultAvailable',
    title: 'Используется',
    sortable: props.tariffPage && props.disabled,
    width: props.tariffPage && $display.lgAndUp.value ? 160 : 130,
    editable: {
      type: EditableHeaderType.Checkbox,
      title: 'Используется',
      editableKey: 'tarification.defaultAvailable',
      disabled: props.disabled,
    },
  },
  {
    key: 'tarification.maxNoSeatQuantity',
    title: 'Туристы без места',
    sortable: props.tariffPage && props.disabled,
    width: props.tariffPage && $display.lgAndUp.value ? 280 : 175,
    editable: {
      type: EditableHeaderType.Input,
      title: 'Туристы без места',
      editableKey: 'tarification.maxNoSeatQuantity',
      disabled: props.disabled,
      maska: placesMask,
    },
  },
  {
    key: 'tarification.pricePerRoom',
    title: 'Цена',
    sortable: props.tariffPage && props.disabled,
    width: props.tariffPage && $display.lgAndUp.value ? 180 : 166,
    editable: {
      type: EditableHeaderType.Select,
      title: 'Цена',
      editableKey: 'tarification.pricePerRoom',
      value: false,
      disabled: props.disabled,
      selectOptions: [
        {
          title: 'за номер',
          value: true,
        },
        {
          title: 'за туриста',
          value: false,
        },
      ],
    },
  },
  {
    key: 'tarification.isNeedConfirm',
    title: 'Под запрос',
    sortable: props.tariffPage && props.disabled,
    width: props.tariffPage ? 160 : 146,
    editable: {
      type: EditableHeaderType.Checkbox,
      title: 'Под запрос',
      editableKey: 'tarification.isNeedConfirm',
      disabled: props.disabled,
    },
  },
]);
</script>

<style scoped lang="scss">
.placing-variant-table {
  &__name {
    display: flex;
    align-items: center;

    & > span {
      @include ellipsis();

      display: inline-block;

      max-width: 100%;
      margin-right: 4px;
    }
  }
}
</style>
